// ArcaOS Prompt detail — templates, governance, evaluation harness, metrics, versions.
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { osFetch } from "../api";
import { Card } from "../shared/panels";
import { StatusBadge } from "./PromptLibrary";
import { ArrowLeft, Save, Send, CheckCircle2, XCircle, RotateCcw, FlaskConical, Loader2, GitCompare, Gauge, Clock, DollarSign, Activity } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Template", "Test & Evaluate", "Metrics", "Versions"];

export default function PromptDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("Template");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => osFetch(`/prompts/${id}`).then(setP).catch(() => setP(null)), [id]);
  useEffect(() => { load(); }, [load]);

  if (p === null) return <p className="text-slate-600 text-sm">Loading…</p>;
  const e = p.entity;

  const action = async (label, path, body) => {
    setBusy(label);
    try { await osFetch(path, { method: "POST", body }); toast.success(`${label} done`); load(); }
    catch (err) { toast.error(err.message); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="os-prompt-detail">
      <Link to="/os/prompts" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2" data-testid="prompt-back"><ArrowLeft size={14} /> Prompt Library</Link>
      <div className="flex items-start justify-between gap-4 mt-3 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl" data-testid="prompt-title">{e.title}</h1>
          <p className="font-mono text-sm text-slate-500 mt-1">{e.name} · <span className="text-[#00F0FF]">{e.category}</span> · {e.surface}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={e.status} />
          {e.published_version && <span className="text-xs font-mono text-emerald-300" data-testid="prompt-live-version">live v{e.published_version}</span>}
        </div>
      </div>

      {/* Governance workflow */}
      <div className="flex items-center gap-2 mt-5 flex-wrap" data-testid="prompt-governance">
        <button onClick={() => action("Submit", `/prompts/${id}/submit`)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs border border-white/10 rounded-xl px-3 py-2 text-slate-300 hover:text-white hover:border-white/30" data-testid="prompt-submit-btn"><Send size={13} /> Submit for review</button>
        <button onClick={() => action("Approve", `/prompts/${id}/approve`)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs border border-emerald-500/30 text-emerald-300 rounded-xl px-3 py-2 hover:bg-emerald-500/10" data-testid="prompt-approve-btn"><CheckCircle2 size={13} /> Approve &amp; publish</button>
        <button onClick={() => action("Reject", `/prompts/${id}/reject`, { reason: "Needs changes" })} disabled={busy} className="inline-flex items-center gap-1.5 text-xs border border-rose-500/30 text-rose-300 rounded-xl px-3 py-2 hover:bg-rose-500/10" data-testid="prompt-reject-btn"><XCircle size={13} /> Reject</button>
      </div>

      <div className="flex gap-2 mt-6 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`text-sm px-3 py-2.5 -mb-px border-b-2 transition-colors ${tab === t ? "border-[#0055FF] text-white" : "border-transparent text-slate-500 hover:text-white"}`} data-testid={`prompt-tab-${t.split(" ")[0].toLowerCase()}`}>{t}</button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "Template" && <TemplateTab p={p} onSaved={load} />}
        {tab === "Test & Evaluate" && <TestTab id={id} p={p} onDone={load} />}
        {tab === "Metrics" && <MetricsTab metrics={p.metrics} />}
        {tab === "Versions" && <VersionsTab id={id} published={e.published_version} onChange={load} />}
      </div>
    </div>
  );
}

function TemplateTab({ p, onSaved }) {
  const e = p.entity;
  const [sys, setSys] = useState(e.system_template || "");
  const [usr, setUsr] = useState(e.user_template || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await osFetch(`/prompts/${e.id}`, { method: "PATCH", body: { system_template: sys, user_template: usr, expected_version: e.current_version } }); toast.success("Saved (new draft version)"); onSaved(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Card title="System template" testid="prompt-system-template">
        <textarea value={sys} onChange={(ev) => setSys(ev.target.value)} rows={10} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-200 outline-none font-mono leading-relaxed" data-testid="prompt-system-input" />
      </Card>
      <Card title="User template (optional)" testid="prompt-user-template">
        <textarea value={usr} onChange={(ev) => setUsr(ev.target.value)} rows={4} placeholder="Optional. Runtime user text is usually built from live data." className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-200 outline-none font-mono" data-testid="prompt-user-input" />
      </Card>
      {e.variables?.length > 0 && (
        <Card title="Variables" testid="prompt-variables">
          <ul className="space-y-1.5">{e.variables.map((v, i) => <li key={i} className="text-sm text-slate-300"><span className="font-mono text-[#00F0FF]">{`{{${v.name}}}`}</span> {v.required && <span className="text-[10px] text-rose-300 ml-1">required</span>} <span className="text-slate-500 ml-2">{v.description}</span></li>)}</ul>
        </Card>
      )}
      <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[#0055FF] text-white font-semibold rounded-xl px-5 py-3 disabled:opacity-40" data-testid="prompt-save-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save draft</button>
    </div>
  );
}

function TestTab({ id, p, onDone }) {
  const [sample, setSample] = useState("");
  const [score, setScore] = useState("");
  const [expected, setExpected] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const body = { sample_user_text: sample, variables: {},
        manual_score: score ? Number(score) : null,
        expected_contains: expected ? expected.split(",").map((s) => s.trim()).filter(Boolean) : null };
      const r = await osFetch(`/prompts/${id}/test`, { method: "POST", body });
      setResult(r); toast.success("Evaluation complete"); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const am = result?.auto_metrics;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card icon={FlaskConical} title="Evaluation harness" testid="prompt-eval-form">
        <label className="text-[11px] font-mono text-slate-500 uppercase">Sample input</label>
        <textarea value={sample} onChange={(e) => setSample(e.target.value)} rows={5} placeholder="Sample user input to run against the current version…" className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none font-mono" data-testid="prompt-eval-sample" />
        <div className="flex gap-3 mt-3">
          <div className="flex-1"><label className="text-[11px] font-mono text-slate-500 uppercase">Manual score (1-10)</label><input type="number" min={1} max={10} value={score} onChange={(e) => setScore(e.target.value)} className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none" data-testid="prompt-eval-score" /></div>
          <div className="flex-[2]"><label className="text-[11px] font-mono text-slate-500 uppercase">Expected to contain (grounding)</label><input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="ArcaCore, confidence" className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none" data-testid="prompt-eval-expected" /></div>
        </div>
        <button onClick={run} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#0055FF] text-white font-semibold rounded-xl px-5 py-3 disabled:opacity-40" data-testid="prompt-eval-run">{busy ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />} Run evaluation</button>
      </Card>
      <Card title="Result" testid="prompt-eval-result">
        {!result ? <p className="text-slate-600 text-sm">Run an evaluation to see output + automatic metrics.</p> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-[11px] font-mono">
              <Metric icon={Clock} label={`${result.latency_ms} ms`} />
              <Metric icon={DollarSign} label={`~$${result.cost_estimate}`} />
              <Metric icon={Gauge} label={`${result.tokens_out} out tok`} />
              <span className={`px-2 py-0.5 rounded-full border ${result.ok ? "text-emerald-300 border-emerald-500/40" : "text-rose-300 border-rose-500/40"}`}>{result.ok ? "success" : "failed"}</span>
            </div>
            {am && (
              <div className="flex flex-wrap gap-1.5 text-[10px] font-mono">
                <Flag ok={am.variable_validation_passed} label="variables valid" />
                <Flag ok={am.json_output_valid} label="json valid" />
                {am.grounding_check && <Flag ok={am.grounding_check.passed} label="grounding" />}
                {am.unresolved_placeholders?.length > 0 && <span className="px-2 py-0.5 rounded-full border text-amber-300 border-amber-500/40">unresolved: {am.unresolved_placeholders.join(", ")}</span>}
              </div>
            )}
            <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-black/40 rounded-xl p-3 max-h-72 overflow-auto" data-testid="prompt-eval-output">{result.output}</pre>
          </div>
        )}
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label }) {
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 text-slate-300"><Icon size={11} /> {label}</span>;
}
function Flag({ ok, label }) {
  return <span className={`px-2 py-0.5 rounded-full border ${ok ? "text-emerald-300 border-emerald-500/40" : "text-rose-300 border-rose-500/40"}`}>{ok ? "✓" : "✕"} {label}</span>;
}

function MetricsTab({ metrics }) {
  if (!metrics) return <p className="text-slate-600 text-sm">No metrics yet.</p>;
  const maxCount = Math.max(1, ...metrics.timeseries.map((t) => t.count));
  return (
    <div className="space-y-4" data-testid="prompt-metrics">
      <div className="grid sm:grid-cols-4 gap-3">
        <Stat icon={Activity} label="Total calls" value={metrics.total_calls} />
        <Stat icon={CheckCircle2} label="Success rate" value={metrics.success_rate != null ? `${metrics.success_rate}%` : "—"} />
        <Stat icon={Clock} label="Avg latency" value={`${metrics.avg_latency_ms} ms`} />
        <Stat icon={DollarSign} label="Est. cost" value={`~$${metrics.total_cost_estimate}`} />
      </div>
      <Card icon={GitCompare} title="Usage by version" testid="prompt-by-version">
        {Object.keys(metrics.by_version).length === 0 ? <p className="text-slate-600 text-sm">No usage recorded yet.</p> : (
          <ul className="space-y-1.5">{Object.entries(metrics.by_version).map(([v, m]) => (
            <li key={v} className="text-sm text-slate-300 flex items-center gap-3"><span className="font-mono text-slate-500 w-16">v{v}</span><span>{m.count} calls</span><span className="text-slate-500">· {m.avg_latency_ms} ms</span><span className="text-slate-500">· ~${m.cost_estimate}</span></li>
          ))}</ul>
        )}
      </Card>
      <Card title="Calls over time" testid="prompt-timeseries">
        {metrics.timeseries.length === 0 ? <p className="text-slate-600 text-sm">No history yet.</p> : (
          <div className="flex items-end gap-1.5 h-28">
            {metrics.timeseries.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1" title={`${t.date}: ${t.count} calls`}>
                <div className="w-full bg-[#0055FF]/60 rounded-t" style={{ height: `${(t.count / maxCount) * 100}%` }} />
                <span className="text-[8px] font-mono text-slate-600 rotate-45 origin-left whitespace-nowrap">{t.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
function Stat({ icon: Icon, label, value }) {
  return <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4"><div className="flex items-center gap-1.5 text-slate-500"><Icon size={13} /><span className="text-[10px] font-mono uppercase tracking-widest">{label}</span></div><p className="text-2xl font-display font-bold mt-1 text-white">{value}</p></div>;
}

function VersionsTab({ id, published, onChange }) {
  const [history, setHistory] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => osFetch(`/prompts/${id}/history`).then(setHistory).catch(() => setHistory([])), [id]);
  useEffect(() => { load(); }, [load]);

  const publish = async (v) => {
    setBusy(v);
    try { await osFetch(`/prompts/${id}/publish/${v}`, { method: "POST" }); toast.success(`Published v${v}`); load(); onChange(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const compare = async (a, b) => {
    try { setCmp(await osFetch(`/prompts/${id}/compare?v_from=${a}&v_to=${b}`)); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card title={`Version history (${history.length})`} testid="prompt-versions">
        <ul className="space-y-2">
          {history.map((h, i) => (
            <li key={h.version} className="flex items-center gap-2 text-sm" data-testid={`prompt-version-${h.version}`}>
              <span className="font-mono text-xs text-slate-500 w-8">v{h.version}</span>
              <span className="text-slate-400 truncate flex-1">{h.reason}</span>
              {h.version === published && <span className="text-[10px] font-mono text-emerald-300">LIVE</span>}
              {history[i + 1] && <button onClick={() => compare(history[i + 1].version, h.version)} className="text-slate-500 hover:text-white" title="Compare with previous"><GitCompare size={13} /></button>}
              {h.version !== published && <button onClick={() => publish(h.version)} disabled={busy === h.version} className="text-[#00F0FF] hover:underline text-xs inline-flex items-center gap-1" data-testid={`prompt-publish-${h.version}`}>{busy === h.version ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Publish</button>}
            </li>
          ))}
        </ul>
      </Card>
      <Card icon={GitCompare} title="Version comparison" testid="prompt-compare">
        {!cmp ? <p className="text-slate-600 text-sm">Click the compare icon on a version to diff it against the previous one.</p> : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Comparing <span className="text-white font-mono">v{cmp.from.version}</span> → <span className="text-white font-mono">v{cmp.to.version}</span> · changed: {cmp.changed_fields.join(", ") || "none"}</p>
            <div className="grid grid-cols-2 gap-2">
              <pre className="text-[11px] text-rose-200/80 whitespace-pre-wrap bg-black/40 rounded-lg p-2 max-h-64 overflow-auto">{cmp.from.system_template}</pre>
              <pre className="text-[11px] text-emerald-200/80 whitespace-pre-wrap bg-black/40 rounded-lg p-2 max-h-64 overflow-auto">{cmp.to.system_template}</pre>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
